import './fristen.css';
import { useMemo, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { amountLabel } from './FilingDialog';
import type { ElsterStand, PeriodRef } from './types';

// Umsatzsteuer-Voranmeldung erstellen (server/src/modules/fristen/elster.ts): Zahlen aus der Buchhaltung eintragen wie
// in ELSTER, die App rechnet die Zahllast. Wer senden darf (Steuerberater, Bilanzbuchhalter, Steuerfachwirt), sendet
// mit dem ELSTER-Zertifikat der Kanzlei; alle anderen geben die Zahlen dem Mandanten, der sie in seiner App prüft und
// mit seinem Zertifikat sendet.

const GRUPPEN: [string, string[]][] = [
  ['Umsätze', ['Kz81', 'Kz86', 'Kz41', 'Kz21', 'Kz45', 'Kz48', 'Kz60']],
  ['Einkäufe aus der EU und Steuer, die der Mandant schuldet', ['Kz89', 'Kz93', 'Kz46', 'Kz47', 'Kz84', 'Kz85']],
  ['Vorsteuer', ['Kz66', 'Kz61', 'Kz67']],
];

// Zahllast in Cent wie auf dem Server (mitZahllast): Bemessungsgrundlagen in Euro × Satz, Rest in Cent
export function zahllast(k: Record<string, number>): number {
  const v = (kz: string) => k[kz] ?? 0;
  return v('Kz81') * 19 + v('Kz86') * 7 + v('Kz89') * 19 + v('Kz93') * 7 + v('Kz47') + v('Kz85') - v('Kz66') - v('Kz61') - v('Kz67') - v('Kz39');
}

export function ElsterDialog({
  clientId,
  clientName,
  period,
  stand,
  onClose,
}: {
  clientId: string;
  clientName: string;
  period: PeriodRef & { title: string; deadline?: string };
  stand: ElsterStand;
  onClose(changed: boolean): void;
}) {
  const toast = useToast();
  const euroKz = useMemo(() => new Set(stand.kz.euro), [stand.kz.euro]);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [land, setLand] = useState(stand.bundesland ?? '');
  const [zert, setZert] = useState<File | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState<'pruefen' | 'senden' | 'mandant' | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sonder = (period.kind === 'Q' && period.n === 4) || (period.kind === 'M' && period.n === 12);
  const gruppen: [string, string[]][] = sonder ? [...GRUPPEN, ['Sondervorauszahlung', ['Kz39']]] : GRUPPEN;

  // Eingaben → Kennzahlen (Bemessungsgrundlagen ganze Euro, sonst Cent); null = ungültig
  const kennzahlen = useMemo(() => {
    const k: Record<string, number> = {};
    for (const [kz, text] of Object.entries(werte)) {
      if (!text.trim()) continue;
      const e = parseEuro(text);
      if (e == null) return { fehler: kz };
      if (euroKz.has(kz) && !Number.isInteger(e)) return { fehler: kz };
      const v = euroKz.has(kz) ? e : Math.round(e * 100);
      if (v) k[kz] = v;
    }
    return { k };
  }, [werte, euroKz]);
  const z = kennzahlen.k ? zahllast(kennzahlen.k) : null;

  function angaben(): Record<string, unknown> | null {
    setError(null);
    setOk(null);
    if (!kennzahlen.k) {
      setError(`Kennzahl ${kennzahlen.fehler!.slice(2)}: Bitte den Betrag prüfen${euroKz.has(kennzahlen.fehler!) ? ' (ganze Euro)' : ''}.`);
      return null;
    }
    if (!land) {
      setError('Bitte das Bundesland des Finanzamts wählen.');
      return null;
    }
    return { kind: period.kind, year: period.year, n: period.n, kennzahlen: kennzahlen.k, bundesland: land };
  }

  async function senden(nurPruefen: boolean) {
    const d = angaben();
    if (!d) return;
    if (!nurPruefen && (!zert || !pin)) return setError('Bitte die Zertifikatsdatei der Kanzlei und die PIN angeben.');
    const fd = new FormData();
    fd.append('data', JSON.stringify({ ...d, nurPruefen }));
    if (zert) fd.append('zertifikat', zert, zert.name);
    fd.append('pin', pin);
    setBusy(nurPruefen ? 'pruefen' : 'senden');
    try {
      const r = await api.post<{ geprueft?: boolean; test?: boolean }>(`/api/kanzlei/ustva/${clientId}/elster`, fd);
      if (r.geprueft) setOk('ELSTER hat die Voranmeldung geprüft: alles in Ordnung.');
      else {
        toast(r.test ? `${period.title} an das Testsystem von ELSTER gesendet` : `${period.title} an ELSTER gesendet · ${clientName} sieht das Protokoll in der App`);
        onClose(true);
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function anMandant(e?: FormEvent) {
    e?.preventDefault();
    const d = angaben();
    if (!d) return;
    setBusy('mandant');
    try {
      await api.post(`/api/kanzlei/ustva/${clientId}/vorbereiten`, d);
      toast(`${period.title} an ${clientName} gegeben · er prüft und sendet in seiner App`);
      onClose(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onClose={() => onClose(false)} title={`Voranmeldung: ${period.title}`} className="ustdlg elsterdlg">
      <p className="note">
        {clientName}
        {period.deadline ? ` · Frist ${fmtDate(period.deadline)}` : ''}
        {stand.taxNumber ? ` · Steuernummer ${stand.taxNumber}` : ' · Steuernummer fehlt in den Stammdaten'}
        {stand.test ? ' · Testbetrieb von ELSTER' : ''}
      </p>
      <form className="form" onSubmit={(e) => (stand.darfSenden ? (e.preventDefault(), void senden(false)) : void anMandant(e))} noValidate>
        <p className="note">Beträge wie in ELSTER: Umsätze in ganzen Euro ohne Steuer, Steuer und Vorsteuer mit Cent. Leere Felder bleiben weg.</p>
        {gruppen.map(([titel, kzs]) => (
          <fieldset key={titel} className="el-gruppe">
            <legend>{titel}</legend>
            {kzs.map((kz) => (
              <label key={kz} className="el-kz">
                <span className="el-nr">{kz.slice(2)}</span>
                <span className="el-txt">{stand.kz.text[kz] ?? kz}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={werte[kz] ?? ''}
                  placeholder={euroKz.has(kz) ? '0' : '0,00'}
                  aria-label={`Kennzahl ${kz.slice(2)}`}
                  onChange={(e) => setWerte((w) => ({ ...w, [kz]: e.target.value }))}
                />
              </label>
            ))}
          </fieldset>
        ))}
        <div className="el-summe">
          <span>Kennzahl 83</span>
          <b>{z === null ? '–' : amountLabel(z / 100)}</b>
        </div>
        <label>
          Bundesland des Finanzamts *
          <select value={land} onChange={(e) => setLand(e.target.value)}>
            <option value="">Bitte wählen</option>
            {stand.laender.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>

        {stand.darfSenden ? (
          <>
            {!stand.kanzleiAdresse && <p className="note ust-err">Die Anschrift der Kanzlei ist auf dem Server noch nicht eingetragen.</p>}
            <div className="row ust-two">
              <label>
                ELSTER-Zertifikat der Kanzlei (.pfx)
                <input type="file" accept=".pfx,.p12" onChange={(e) => setZert(e.target.files?.[0] ?? null)} />
              </label>
              <label>
                PIN
                <input type="password" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
              </label>
            </div>
            <p className="note">Datei und PIN nutzt die App nur für dieses Senden und speichert sie nicht.</p>
          </>
        ) : (
          <p className="note">
            Senden dürfen nur Steuerberater, Bilanzbuchhalter und Steuerfachwirte. Sie geben die Zahlen deshalb {clientName}: Er
            sieht sie in seiner App, prüft sie und sendet mit seinem ELSTER-Zertifikat.
          </p>
        )}
        {ok && <p className="note el-ok">{ok}</p>}
        {error && (
          <p className="note ust-err" role="alert">
            {error}
          </p>
        )}
        <div className="dlg-actions">
          {stand.darfSenden ? (
            <>
              <button type="submit" className="btn dark" disabled={!!busy}>
                {busy === 'senden' ? 'Sendet …' : 'An ELSTER senden'}
              </button>
              <button type="button" className="btn" disabled={!!busy} onClick={() => void senden(true)}>
                {busy === 'pruefen' ? 'Prüft …' : 'Erst prüfen'}
              </button>
              <button type="button" className="btn" disabled={!!busy} onClick={() => void anMandant()}>
                Dem Mandanten geben
              </button>
            </>
          ) : (
            <>
              <button type="submit" className="btn dark" disabled={!!busy}>
                {busy === 'mandant' ? 'Gibt weiter …' : 'Dem Mandanten zum Senden geben'}
              </button>
              <button type="button" className="btn" onClick={() => onClose(false)}>
                Abbrechen
              </button>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}
